import { FC, useCallback, useEffect, useMemo } from 'react';
import { IAgentReport } from '../../models/reports.internal';
import { reduceWithSum } from '../../utils/arrayUtils';
import { objectValues } from '../../utils/objectUtils';
import { nameof } from '../../utils/stringUtils';
import DefaultSpinner from '../shared/DefaultSpinner';
import useSortedListItems, { SortingProp } from '../../shared/hooks/useSortedListItems';
import Grid from '../shared/Grid';
import { HintText } from '../../shared/styled-components';
import getTotalWorkItemCounts from '../../calculations/getTotalWorkItemCounts';
import { MemberFilterOptions, memberSortOptions } from '../../containers/MemberSortAndFilterModal';
import AgentCard from './AgentCard';

interface IProps {
  isWideScreen: boolean;
  memberReports: IAgentReport[];
  searchText: string;
  filters: MemberFilterOptions;
  isSortedDescending: boolean;
  sortOptionKey: string;
}

const TeamAgentCards: FC<IProps> = ({ memberReports, searchText, filters, isSortedDescending, sortOptionKey, isWideScreen }) => {
  const filteredMembers = useMemo(
    () => {
      const baseFiltered = memberReports.filter(m => reduceWithSum(objectValues(m.timeSpentInStates)) > 0);

      let extraFiltered = baseFiltered;

      if (searchText) {
        const namePartial = searchText.toLocaleLowerCase();
        extraFiltered = extraFiltered.filter(ar => ar.name.toLocaleLowerCase().includes(namePartial));
      }

      if (filters.hasCompletedTasks)
        extraFiltered = extraFiltered.filter(ar => getTotalWorkItemCounts(ar.workItemsByMedia).handled > 0);

      extraFiltered = extraFiltered.filter(ar => filters.states[ar.currentState]);

      return extraFiltered;
    },
    [filters, memberReports, searchText],
  );

  const getSortProp = useCallback(
    (key: string): SortingProp<IAgentReport> => {
      const sortProp = memberSortOptions[key];
      if (typeof sortProp === 'object')
        return { key, ...sortProp };
      else
        return sortProp;
    },
    [],
  );

  const { sortedItems: sortedMembers, sortBy } = useSortedListItems(filteredMembers, getSortProp(sortOptionKey));

  useEffect(
    () => sortBy(getSortProp(sortOptionKey), isSortedDescending ? 'DESC' : 'ASC'),
    [getSortProp, isSortedDescending, sortBy, sortOptionKey],
  );

  return (
    memberReports.length > 0
      ? sortedMembers.length > 0
        ? (
          <Grid columns={{ mode: 'equal', count: isWideScreen ? 3 : 2 }} gap="0.5rem">
            {sortedMembers.map(m => <AgentCard key={m.id} agent={m} />)}
          </Grid>
        )
        : <HintText centered italic height="100%">Nothing to show</HintText>
      : <DefaultSpinner fullHeight label="Waiting for  agent reports ..." />
  );
};

TeamAgentCards.displayName = nameof({ TeamAgentCards });
export default TeamAgentCards;